import { useMemo, useState } from 'react';
import { Plus, Trash2, ExternalLink, Pencil } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { DataTable } from '@/components/tables/DataTable';
import { EditSheet } from '@/components/layout/EditSheet';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useAdminPosts, useUpsertPost, useDeletePost, type PostRow } from '@/lib/admin-queries';
import { BlogEditForm } from '@/features/blog/BlogEditForm';
import { formatDate } from '@/lib/utils';
import { toast } from '@/components/ui/Toast';

const EMPTY: PostRow = {
  slug: '',
  title: '',
  excerpt: '',
  body_markdown: '',
  hero_media_id: null,
  author_id: null,
  category: 'Technology',
  tags: [],
  reading_time_min: null,
  status: 'draft',
  scheduled_at: null,
  published_at: null,
  meta_title: '',
  meta_description: '',
};

export function BlogPage() {
  const { data: rows = [], isLoading } = useAdminPosts();
  const upsert = useUpsertPost();
  const remove = useDeletePost();
  const [editing, setEditing] = useState<PostRow | null>(null);
  const [deleting, setDeleting] = useState<PostRow | null>(null);

  const columns = useMemo(
    () => [
      {
        key: 'title',
        header: 'Title',
        cell: (r: PostRow) => (
          <div>
            <p className="font-medium">{r.title}</p>
            <p className="text-xs text-fg-subtle">{r.slug}</p>
          </div>
        ),
      },
      {
        key: 'category',
        header: 'Category',
        cell: (r: PostRow) => <Badge variant="gold">{r.category}</Badge>,
      },
      {
        key: 'status',
        header: 'Status',
        cell: (r: PostRow) => (
          <Badge
            variant={
              r.status === 'published' ? 'success' : r.status === 'scheduled' ? 'warning' : r.status === 'archived' ? 'default' : 'outline'
            }
          >
            {r.status}
          </Badge>
        ),
      },
      {
        key: 'reading',
        header: 'Read',
        cell: (r: PostRow) => (
          <span className="text-xs text-fg-muted">{r.reading_time_min ? `${r.reading_time_min} min` : '—'}</span>
        ),
      },
      {
        key: 'published',
        header: 'Published',
        cell: (r: PostRow) => (
          <span className="text-xs text-fg-muted">{r.published_at ? formatDate(r.published_at) : '—'}</span>
        ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right' as const,
        cell: (r: PostRow) => (
          <div className="flex items-center justify-end gap-1">
            {r.status === 'published' && (
              <a
                href={`https://vonlof.co/blog/${r.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View live"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            <button
              type="button"
              aria-label="Edit"
              onClick={(e) => {
                e.stopPropagation();
                setEditing(r);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                setDeleting(r);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-danger/15 hover:text-danger"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  async function onSave(row: PostRow) {
    try {
      await upsert.mutateAsync(row);
      toast.success(row.id ? 'Post updated' : 'Post created');
      setEditing(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function onConfirmDelete() {
    if (!deleting?.id) return;
    try {
      await remove.mutateAsync(deleting.id);
      toast.success('Post deleted');
      setDeleting(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Blog</p>
          <h1 className="mt-2 display-serif text-display-l text-fg">Posts</h1>
          <p className="mt-2 text-sm text-fg-muted">Write, schedule, and publish articles.</p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing({ ...EMPTY })}>
          New post
        </Button>
      </header>

      {isLoading ? (
        <Card>
          <div className="space-y-3 p-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </Card>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => r.id ?? r.slug}
          onRowClick={(r) => setEditing(r)}
          empty="No posts yet. Click New post to write one."
        />
      )}

      <EditSheet
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        title={editing?.id ? 'Edit post' : 'New post'}
        description="Write in Markdown. Tags are comma-separated."
        size="xl"
      >
        {editing && (
          <BlogEditForm
            value={editing}
            submitting={upsert.isPending}
            onSubmit={onSave}
            onCancel={() => setEditing(null)}
          />
        )}
      </EditSheet>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete this post?"
        description={deleting ? `"${deleting.title}" will be removed. This cannot be undone.` : ''}
        confirmLabel="Delete post"
        destructive
        loading={remove.isPending}
        onConfirm={onConfirmDelete}
      />
    </div>
  );
}